import { downloadXlsx, dateStamp } from './download-xlsx';

/** Builders that map report procedure output → flat Turkish-headed rows and
 *  download an .xlsx. Data is already fully filtered server-side; this only
 *  serializes it. */

const d = (v: Date | string): string => {
  const dt = typeof v === 'string' ? new Date(v) : v;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}.${p(dt.getMonth() + 1)}.${dt.getFullYear()}`;
};

type LedgerItem = {
  movementDate: Date | string; voucherNo: string; typeLabel: string; departmentName: string | null;
  productCode: string; sku: string; productName: string; brandName: string; unit: string;
  inQty: number; outQty: number; inValue: number; outValue: number;
};
export function exportStockLedger(items: LedgerItem[], totals: { inQty: number; outQty: number; inValue: number; outValue: number }): void {
  const rows: Record<string, unknown>[] = items.map((m) => ({
    Tarih: d(m.movementDate), 'Fiş No': m.voucherNo, Tür: m.typeLabel, Departman: m.departmentName ?? '',
    Kod: m.productCode, SKU: m.sku, Ürün: m.productName, Marka: m.brandName, Birim: m.unit,
    'Giriş Mik.': m.inQty, 'Çıkış Mik.': m.outQty, 'Giriş Değer (TL)': m.inValue, 'Çıkış Değer (TL)': m.outValue,
  }));
  rows.push({ Tarih: '', 'Fiş No': '', Tür: 'GENEL TOPLAM', Departman: '', Kod: '', SKU: '', Ürün: '', Marka: '', Birim: '',
    'Giriş Mik.': totals.inQty, 'Çıkış Mik.': totals.outQty, 'Giriş Değer (TL)': totals.inValue, 'Çıkış Değer (TL)': totals.outValue });
  downloadXlsx(rows, 'Stok Hareket', `stok-hareket-${dateStamp()}.xlsx`);
}

type StatusItem = {
  productCode: string; sku: string; productName: string; brandName: string; unit: string;
  byType: Record<string, { qty: number; value: number }>;
  currentQty: number; currentValue: number; cogs: number; salesQty: number; salesValue: number; grossProfit: number;
};
export function exportStockStatus(items: StatusItem[], totals: { currentValue: number; cogs: number; salesValue: number; grossProfit: number }): void {
  const q = (i: StatusItem, t: string) => i.byType[t]?.qty ?? 0;
  const rows: Record<string, unknown>[] = items.map((i) => ({
    Kod: i.productCode, SKU: i.sku, Ürün: i.productName, Marka: i.brandName, Birim: i.unit,
    Devir: q(i, 'INITIAL_STOCK'), Alış: q(i, 'PURCHASE_RECEIPT'), 'Müşteri İade': q(i, 'RETURN_FROM_CUSTOMER'),
    'Transfer Giriş': q(i, 'TRANSFER_IN'), 'Transfer Çıkış': q(i, 'TRANSFER_OUT'),
    Sarf: q(i, 'SCRAP') + q(i, 'ADJUSTMENT_OUT'), 'Satıcıya İade': q(i, 'RETURN_TO_SUPPLIER'), Satış: q(i, 'SALES_ISSUE'),
    'Mevcut Stok': i.currentQty, 'Maliyet (TL)': i.cogs, 'Satış Değeri (TL)': i.salesValue, 'Kâr/Zarar (TL)': i.grossProfit, 'Stok Değeri (TL)': i.currentValue,
  }));
  rows.push({ Kod: '', SKU: '', Ürün: 'GENEL TOPLAM', Marka: '', Birim: '', Devir: '', Alış: '', 'Müşteri İade': '',
    'Transfer Giriş': '', 'Transfer Çıkış': '', Sarf: '', 'Satıcıya İade': '', Satış: '',
    'Mevcut Stok': '', 'Maliyet (TL)': totals.cogs, 'Satış Değeri (TL)': totals.salesValue, 'Kâr/Zarar (TL)': totals.grossProfit, 'Stok Değeri (TL)': totals.currentValue });
  downloadXlsx(rows, 'Stok Durum', `stok-durum-${dateStamp()}.xlsx`);
}

type MizanWarehouse = {
  warehouseName: string;
  rows: Array<{ productCode: string; sku: string; productName: string; brandName: string; unit: string;
    openingQty: number; openingValue: number; inQty: number; inValue: number; outQty: number; outValue: number; closingQty: number; closingValue: number }>;
  subtotal: { openingValue: number; inValue: number; outValue: number; closingValue: number };
};
export function exportStockMizan(warehouses: MizanWarehouse[]): void {
  const rows: Record<string, unknown>[] = [];
  for (const w of warehouses) {
    for (const r of w.rows) {
      rows.push({
        Depo: w.warehouseName, Kod: r.productCode, SKU: r.sku, Ürün: r.productName, Marka: r.brandName, Birim: r.unit,
        'Açılış Mik.': r.openingQty, 'Açılış Değer (TL)': r.openingValue, 'Giren Mik.': r.inQty, 'Giren Değer (TL)': r.inValue,
        'Çıkan Mik.': r.outQty, 'Çıkan Değer (TL)': r.outValue, 'Kalan Mik.': r.closingQty, 'Kalan Değer (TL)': r.closingValue,
      });
    }
    rows.push({ Depo: `${w.warehouseName} TOPLAMI`, Kod: '', SKU: '', Ürün: '', Marka: '', Birim: '',
      'Açılış Mik.': '', 'Açılış Değer (TL)': w.subtotal.openingValue, 'Giren Mik.': '', 'Giren Değer (TL)': w.subtotal.inValue,
      'Çıkan Mik.': '', 'Çıkan Değer (TL)': w.subtotal.outValue, 'Kalan Mik.': '', 'Kalan Değer (TL)': w.subtotal.closingValue });
  }
  downloadXlsx(rows, 'Stok Mizan', `stok-mizan-${dateStamp()}.xlsx`);
}

type PnlCategory = { label: string; accounts: Array<{ code: string; name: string; amount: number }>; total: number };
export function exportProfitLoss(categories: PnlCategory[], net: number): void {
  const rows: Record<string, unknown>[] = [];
  for (const c of categories) {
    for (const a of c.accounts) rows.push({ Kategori: c.label, 'Hesap Kodu': a.code, 'Hesap Adı': a.name, 'Tutar (TL)': a.amount });
    rows.push({ Kategori: c.label, 'Hesap Kodu': '', 'Hesap Adı': `${c.label} TOPLAM`, 'Tutar (TL)': c.total });
  }
  rows.push({ Kategori: '', 'Hesap Kodu': '', 'Hesap Adı': 'NET KÂR / ZARAR', 'Tutar (TL)': net });
  downloadXlsx(rows, 'Kar-Zarar', `kar-zarar-${dateStamp()}.xlsx`);
}
